import { useEffect, useState } from "react";
import { CreateActorButton } from "../custom-digital-human";
import { useDownload } from "@/hooks/use-download";
import { useCradSize } from "@/hooks/use-card-size";
import { VideoPlayModal } from "@/components/assets-modal-video-play";
import { ActorVideoCard } from "./actor-video-card";
import { assetsActorHooks } from "@/manager";
import { useTranslation } from "react-i18next";

interface SkeletonProps {
  columns: number;
  cardWidth: number;
}

// 骨架屏容器组件
function Skeleton({ columns }: SkeletonProps) {
  return (
    <div className="relative bg-white p-8 h-full w-full overflow-hidden ">
      <div className="h-full overflow-y-auto">
        <div
          className="grid gap-x-4 gap-y-18 mb-20"
          style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
        >
          {Array.from({ length: 40 }).map((_, index) => (
            <div key={index} className="w-full rounded-sm animate-pulse">
              <div className="w-full aspect-[9/16] bg-gray-300 rounded-sm mb-4"></div>
              <div className="h-3 bg-gray-300 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Actor 列表页 */
export const AssetsActor = () => {
  const { t } = useTranslation();
  const { actors, loading, actorRename } = assetsActorHooks.useData();
  const { regenerateActor } = assetsActorHooks.useRegenerateActor();
  const { deleteActor } = assetsActorHooks.useDeleteActor();

  assetsActorHooks.usePollingCleanup();

  const { ref, columns, width, height } = useCradSize({
    min: 120,
    max: 192,
    gap: 16,
  });

  const { download } = useDownload();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [cardWidth, setCardWidth] = useState(171);
  const [cardHeight, setCardHeight] = useState(303);

  const handleVideoClick = (index: number) => {
    console.log({ index });
    setSelectedVideoIndex(index);
    setModalOpen(true);
  };

  // 下载
  const handleDownload = (video: Actor) => {
    const videoUrl = video.video;
    if (!videoUrl) {
      return;
    }
    const filename = `${video.name}_${Date.now()}.mp4`;
    const imageUrl = video.image;
    download(videoUrl, filename, imageUrl);
  };

  // 重命名
  const handleRename = async (id: string, name: string) => {
    await actorRename({ id, name });
  };

  // 重新生成
  const handleRegenerate = async (actor: Actor) => {
    await regenerateActor(actor);
  }

  // 删除
  const handleDelete = async (actor: Actor) => {
    await deleteActor(actor.id);
  }

  if (loading) {
    return <Skeleton columns={columns} cardWidth={cardWidth} />;
  }

  return (
    <div className="relative bg-white p-8 pt-3 h-full w-full overflow-hidden ">
      <header className="mb-[18px] z-[50]">
        <div className="text-[20px] font-bold">{t('assets.actor')}</div>
      </header>
      <div className="h-full overflow-y-auto">
        <div
          ref={ref}
          className="grid gap-x-4 gap-y-18 mb-20"
          style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
        >
          <CreateActorButton
            iconClassname="size-6"
            onCreate={() => { }}
            textClassName="text-sm font-medium font-normal"
          />
          {actors.map((actor, index) => {
            return (
              <ActorVideoCard
                key={`${actor.id}_${index}`}
                actor={actor}
                name={actor.name}
                url={actor.video}
                poster={actor.image}
                status={actor.generateStatus}
                createdAt={actor.createdAt}
                onClick={() => actor.video && handleVideoClick(index)}
                onRename={(name) => handleRename(actor.id, name)}
                onDownload={() => handleDownload(actor)}
                onRegenerate={() => handleRegenerate(actor)}
                onDelete={() => handleDelete(actor)}
              />
            );
          })}
        </div>
        {modalOpen && (
          <VideoPlayModal
            open={modalOpen}
            videos={actors}
            initialIndex={selectedVideoIndex}
            onOpenChange={setModalOpen}
            onDownload={(video) => handleDownload(video)}
          />
        )}
      </div>
    </div>
  );
};
